import { useState, useRef } from 'react';
import { omit, pick } from 'lodash';
import { Platform, App, AppOption, Tracker, TrackerOption, RequestStatus, UseApps } from '../types';
import { getApps, getApp, getAppTrackers } from '../helpers/api';

export const useApps = (): UseApps => {
  const [options, setOptions] = useState<AppOption[]>([]);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const updateDetails = (token: string, updates: Partial<AppOption>) =>
    new Promise<AppOption | void>(resolve => {
      setOptions(_options => {
        const index = _options.findIndex(item => item.value === token);
        if (index === -1) {
          resolve();
          return _options;
        }

        const updatedOption = { ..._options[index], ...updates };
        resolve(updatedOption);
        return _options.map((option, optionIndex) => (optionIndex === index ? updatedOption : option));
      });
    });

  return {
    options,
    fetchDetails: token => {
      const option = optionsRef.current.find(option => option.value === token);
      if (!option) {
        return Promise.reject(`App with token=${token} is not available`);
      }

      if (option.detailsStatus === RequestStatus.fetched) {
        return Promise.resolve(option);
      }

      updateDetails(token, { detailsStatus: RequestStatus.fetching });

      return Promise.all([getApp(token), getAppTrackers(token)])
        .then(([app, trackers]) =>
          updateDetails(token, { detailsStatus: RequestStatus.fetched, ...formatAppFields(app, trackers) }),
        )
        .catch(() => updateDetails(token, { detailsStatus: RequestStatus.failed }));
    },
    fetch: () =>
      getApps().then(apps => {
        apps.sort((a1, a2) => (a1.name > a2.name ? 1 : -1));
        setOptions(appsToOptions(apps));
      }),
    setAvailableTokens: tokens => {
      setOptions(_options => _options.filter(option => tokens.includes(option.value)));
    },
  };
};

const getAppPlatform = (app: App) => {
  const { android, ios } = app.platforms;
  if (ios?.configured && android?.configured) return Platform.multi;
  if (ios?.configured && !android?.configured) return Platform.ios;
  if (!ios?.configured && android?.configured) return Platform.android;
  return undefined;
};

const formatAppOption = (app: App, platform: Platform): AppOption => ({
  label: `${app.name} (${app.app_token})`,
  value: app.app_token,
  platform,
  dataResidency: undefined,
  trackerOptions: [],
  detailsStatus: RequestStatus.idle,
});

export const formatTrackerOption = (tracker: Tracker): TrackerOption => {
  const { campaign, adgroup, creative } = tracker.settings?.campaign_structure || {};
  const { gps_adid: gpsAdid, idfa, label, ...trackingParameters } = omit(tracker.settings?.tracking_parameters, [
    's2s',
    'campaign_id',
    'publisher_id',
    'affiliate_id',
    'subpublisher_id',
    'creative_id',
  ]);
  return {
    label: `${tracker.label} (${tracker.token})`,
    value: tracker.token,
    settings: {
      label,
      campaign,
      adgroup,
      creative,
      idfa,
      gpsAdid,
    },
    additionalParameters: {
      ...trackingParameters,
      ...pick(tracker.settings?.cost_data_parameters, ['cost_id', 'cost_type', 'cost_amount', 'cost_currency']),
    },
  };
};

const appsToOptions = (apps: App[]) => {
  return apps
    .filter(app => app.permissions.update_settings)
    .reduce<AppOption[]>((result, app) => {
      const platform = getAppPlatform(app);
      if (platform) result.push(formatAppOption(app, platform));
      return result;
    }, []);
};

const getRawUniversalLink = (app: App, platform: Platform | undefined, dataResidency: string | undefined) => {
  const { subdomain } = app;
  const residencyDomain = dataResidency ? `.${dataResidency}` : '';
  if (subdomain?.subdomain) {
    return `${subdomain.subdomain}${residencyDomain}.${subdomain.domain}`;
  }

  const { ios } = app.platforms;
  return platform &&
    [Platform.ios, Platform.multi].includes(platform) &&
    ios?.app_scheme &&
    ios?.short_universal_link_token
    ? `${ios.short_universal_link_token}${residencyDomain}.adj.st`
    : '';
};

const formatAppFields = (app: App, trackers: Tracker[]): Partial<AppOption> => {
  const { ios, android } = app.platforms;
  const platform = getAppPlatform(app);
  const dataResidency = app.data_residency_region || undefined;
  return {
    platform,
    dataResidency,
    rawUniversalLink: getRawUniversalLink(app, platform, dataResidency),
    iosAppScheme: platform === Platform.ios && ios?.app_scheme ? `${ios?.app_scheme}://` : '',
    androidAppScheme:
      [Platform.android, Platform.multi].includes(platform!) && android?.app_scheme ? `${android?.app_scheme}://` : '',
    trackerOptions: trackers.map(formatTrackerOption),
  };
};
